import { useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Snackbar as MuiSnackbar } from '@mui/material';
import { type ConnectedProps, connect } from 'react-redux';
import { API_ERROR_EVENT } from '@/core/utils/apiRequest';
import { type RootState } from '@/state';
import {
  isSnackbarOpen,
  snackbarType,
  snackbarMessage,
  closeSnackbar,
  openSnackbar,
} from '../state';
import styles from './styles';

type Props = ConnectedProps<typeof connector>;

const iconByType = {
  success: <CheckIcon sx={{ fontSize: 18 }} />,
  error: <ErrorOutlineIcon sx={{ fontSize: 18 }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 18 }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,
};

const Snackbar = ({
  isSnackbarOpen,
  snackbarType,
  snackbarMessage,
  closeSnackbar,
  openSnackbar,
}: Props) => {
  useEffect(() => {
    const handleApiError = (event: Event) => {
      const { message } = (event as CustomEvent<{ message?: string }>).detail;
      if (!message) return;

      openSnackbar({
        snackbarType: 'error',
        snackbarMessage: message,
      });
    };

    window.addEventListener(API_ERROR_EVENT, handleApiError);

    return () => {
      window.removeEventListener(API_ERROR_EVENT, handleApiError);
    };
  }, [openSnackbar]);

  const handleClose = () => {
    closeSnackbar();
  };

  return (
    <MuiSnackbar
      key={snackbarMessage}
      sx={styles.root}
      slots={{
        clickAwayListener: ({ children }) => children,
      }}
      open={isSnackbarOpen}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={6000}
    >
      <Box sx={styles.toast} role="status" aria-live="polite">
        <Box sx={styles.rail(snackbarType)} />
        <Box sx={styles.iconWrap(snackbarType)}>{iconByType[snackbarType]}</Box>

        <Box component="span" sx={styles.message}>
          {snackbarMessage}
        </Box>

        <IconButton
          aria-label="Close notification"
          onClick={handleClose}
          sx={styles.closeButton}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>
    </MuiSnackbar>
  );
};

const connector = connect(
  (state: RootState) => ({
    isSnackbarOpen: isSnackbarOpen(state),
    snackbarType: snackbarType(state),
    snackbarMessage: snackbarMessage(state),
  }),
  { closeSnackbar, openSnackbar },
);

export default connector(Snackbar);
